import { Injectable } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { QuoteParamsParserService } from '@common/util-foundation';
import {
  ApplianceDetailsFormDataProvider,
  ApplianceDetailsQueryParamsForm,
  ApplianceFormField,
  ApplianceFormSubmit,
  ParseResponse,
  ParseResult,
} from '@common/util-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ApplianceDetailsQueryParamsFormDataProvider
  implements ApplianceDetailsFormDataProvider
{
  priority = 0;
  private readonly validators = [Validators.required];
  private readonly priceValidators = [
    ...this.validators,
    Validators.minLength(2),
    Validators.maxLength(4),
  ];
  private readonly yesNoValidators = [
    ...this.validators,
    Validators.pattern(/^(yes|no)$/i),
  ];
  private readonly yearStringValidators = [
    ...this.validators,
    Validators.minLength(4),
    Validators.maxLength(4),
  ];
  private readonly monthStringValidators = [
    ...this.validators,
    Validators.minLength(1),
    Validators.maxLength(2),
  ];

  constructor(
    private quoteParamsParserService: QuoteParamsParserService<ApplianceDetailsQueryParamsForm>
  ) {}

  getApplianceDetailsForm(): Observable<ParseResponse<ApplianceFormSubmit>> {
    const applianceDetailsForm$ = this.quoteParamsParserService.parse({
      applianceCode: this.validators,
      brandCode: this.validators,
      purchasePrice: this.priceValidators,
      goodWorkingOrder: this.yesNoValidators,
      underGuarantee: this.yesNoValidators,
      month: this.monthStringValidators,
      year: this.yearStringValidators,
      guaranteeDuration: [Validators.pattern(/^[1,2]$/)],
      applianceCategory: [],
      showErrors: [],
    });

    return applianceDetailsForm$.pipe(
      map((applianceDetailsParsed) =>
        this.castApplianceForm(applianceDetailsParsed)
      )
    );
  }

  private castApplianceForm(
    parsedResponse: ParseResponse<ApplianceDetailsQueryParamsForm>
  ): ParseResponse<ApplianceFormSubmit> {
    const applianceFormSubmit = this.createFormSubmit(parsedResponse);
    let newParsedResponse: ParseResponse<ApplianceFormSubmit> = {
      model: applianceFormSubmit,
      result: parsedResponse.result,
      form: parsedResponse.form,
    };
    newParsedResponse = this.adaptFormGroup(
      newParsedResponse,
      applianceFormSubmit
    );
    return newParsedResponse;
  }

  createFormSubmit(
    parsedResponse: ParseResponse<ApplianceDetailsQueryParamsForm>
  ): ApplianceFormSubmit & Pick<ApplianceDetailsQueryParamsForm, 'showErrors'> {
    const parsedResponseModel = parsedResponse.model;
    const formFieldList = [
      'applianceCode',
      'brandCode',
      ApplianceFormField.GoodWorkingOrder,
      ApplianceFormField.PurchasePrice,
      ApplianceFormField.UnderGuarantee,
      'applianceCategory',
      'showErrors',
    ];
    const applianceFormSubmit = formFieldList.reduce(
      (acc, curr) => {
        const formField = this.getValuesWhenFormFieldIsValid(
          parsedResponse,
          curr
        );
        return {
          ...acc,
          [curr]: formField,
        };
      },
      {
        monthYearDate: {
          month: parsedResponse.form?.get('month')?.valid
            ? Number(parsedResponseModel?.month)
            : 0,
          year: parsedResponse.form?.get('year')?.valid
            ? Number(parsedResponseModel?.year)
            : 0,
        },
        [ApplianceFormField.GuaranteeDuration]: Number(
          parsedResponse?.model?.guaranteeDuration
        ),
      }
    );
    return applianceFormSubmit as ApplianceFormSubmit &
      Pick<ApplianceDetailsQueryParamsForm, 'showErrors'>;
  }

  private getValuesWhenFormFieldIsValid(
    parsedResponse: ParseResponse<ApplianceDetailsQueryParamsForm>,
    formField: string
  ): string {
    return parsedResponse.form?.get(formField)?.value &&
      parsedResponse.form?.get(formField)?.valid
      ? parsedResponse.form?.get(formField)?.value
      : '';
  }

  adaptFormGroup(
    parsedResponse: ParseResponse<ApplianceFormSubmit>,
    applianceFormSubmit: ApplianceFormSubmit
  ) {
    parsedResponse.form?.addControl(
      ApplianceFormField.PurchaseDate,
      new UntypedFormControl(
        {
          month: applianceFormSubmit.monthYearDate.month,
          year: applianceFormSubmit.monthYearDate.year,
        },
        [...this.validators, this.numberDateValidator]
      )
    );
    parsedResponse.form?.removeControl('year');
    parsedResponse.form?.removeControl('month');
    parsedResponse.form?.removeControl('showErrors');
    if (parsedResponse.form?.invalid) {
      parsedResponse.result = ParseResult.Failure;
    }
    return parsedResponse;
  }

  private numberDateValidator(control: UntypedFormControl) {
    if (control.value.month < 1 || control.value.month > 12) {
      return {
        invalidMonth: true,
      };
    }
    if (
      control.value.year < 2000 ||
      control.value.year > new Date().getFullYear()
    ) {
      return {
        invalidYear: true,
      };
    }
    return null;
  }
}
